import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, CheckCircle2, Award } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import SEO from "../components/SEO";
import TurnstileField from "../components/TurnstileField";
import { api, apiErrorMessage } from "../lib/api";
import { formatWebinarDate } from "../lib/webinarFormat";

/**
 * Public per-webinar certificate (adeverință) request page — /adeverinta/:slug.
 * Linked to the attendee's registration server-side by (slug + email); the
 * attendance check + approval happen in admin.
 */
export default function CerereAdeverinta() {
  const { slug } = useParams();
  const [webinar, setWebinar] = useState(null);
  const [loadingWebinar, setLoadingWebinar] = useState(true);
  const [form, setForm] = useState({ full_name: "", email: "", school: "", county: "" });
  const [consent, setConsent] = useState(false);
  const [token, setToken] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null); // success message

  useEffect(() => {
    let cancelled = false;
    api.get(`/certificates/webinar-info`, { params: { slug } })
      .then((r) => { if (!cancelled) setWebinar(r.data?.webinar || null); })
      .catch(() => { if (!cancelled) setWebinar(null); })
      .finally(() => { if (!cancelled) setLoadingWebinar(false); });
    return () => { cancelled = true; };
  }, [slug]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!consent) { toast.error("Te rugăm să accepți prelucrarea datelor."); return; }
    if (!token) { toast.error("Te rugăm să confirmi că nu ești robot."); return; }
    setSubmitting(true);
    try {
      const r = await api.post("/certificate-requests", {
        slug, ...form, consent, turnstile_token: token,
      });
      setDone(r.data?.message || "Cererea ta a fost înregistrată.");
    } catch (err) {
      toast.error(apiErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-[#F5F6F6]">
      <SEO title="Cerere adeverință | FPED" noindex />
      <Header />
      <main className="flex-1 w-full max-w-2xl mx-auto px-4 py-12">
        <div className="inline-flex items-center gap-2 text-[#965EA7] font-semibold mb-2">
          <Award size={20} /> Adeverință de participare
        </div>

        {loadingWebinar ? (
          <div className="py-16 text-center text-[#6B7280]"><Loader2 className="inline animate-spin" size={18} /> Se încarcă...</div>
        ) : !webinar ? (
          <div className="bg-white border border-[#E5E7EB] rounded-2xl p-6 text-[#373A45]">
            Nu am găsit acest webinar. Verifică linkul primit pe email.
          </div>
        ) : done ? (
          <div className="bg-white border border-[#E5E7EB] rounded-2xl p-8 text-center" data-testid="certificate-request-done">
            <CheckCircle2 size={48} className="text-[#519872] mx-auto mb-3" />
            <h1 className="font-display text-xl font-bold text-[#373A45] mb-2">Cerere trimisă!</h1>
            <p className="text-[#6B7280]">{done}</p>
          </div>
        ) : (
          <>
            <h1 className="font-display text-2xl font-bold text-[#373A45]">
              {webinar.title}
            </h1>
            <p className="text-sm text-[#6B7280] mt-1 mb-6">
              {formatWebinarDate(webinar.date)} · completează datele pentru adeverința de participare
            </p>

            <form onSubmit={submit} className="bg-white border border-[#E5E7EB] rounded-2xl p-5 md:p-6 space-y-4" data-testid="certificate-request-form">
              <Field label="Nume complet" required>
                <input required value={form.full_name} onChange={set("full_name")}
                  className="w-full rounded-lg border border-[#E5E7EB] px-3 py-2 text-sm" placeholder="Ex: Maria Popescu" />
              </Field>
              <Field label="Email (cel folosit la înscriere)" required>
                <input required type="email" value={form.email} onChange={set("email")}
                  className="w-full rounded-lg border border-[#E5E7EB] px-3 py-2 text-sm" placeholder="nume@email.ro" />
              </Field>
              <Field label="Unitatea de învățământ" required>
                <input required value={form.school} onChange={set("school")}
                  className="w-full rounded-lg border border-[#E5E7EB] px-3 py-2 text-sm" placeholder="Ex: Colegiul Național ..." />
              </Field>
              <Field label="Județ">
                <input value={form.county} onChange={set("county")}
                  className="w-full rounded-lg border border-[#E5E7EB] px-3 py-2 text-sm" placeholder="Ex: Cluj" />
              </Field>

              <label className="flex items-start gap-2 text-sm text-[#373A45]">
                <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} className="mt-1" />
                <span>Sunt de acord cu prelucrarea datelor pentru emiterea adeverinței de participare.</span>
              </label>

              <TurnstileField onVerify={setToken} onExpire={() => setToken(null)} onError={() => setToken(null)} />

              <button type="submit" disabled={submitting}
                className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#965EA7] hover:bg-[#7A4986] text-white font-semibold px-5 py-2.5 text-sm disabled:opacity-60"
                data-testid="certificate-request-submit">
                {submitting ? <Loader2 className="animate-spin" size={16} /> : <Award size={16} />}
                Solicită adeverința
              </button>
              <p className="text-xs text-[#9CA3AF] text-center">
                Adeverința se emite doar pentru participanții care au fost prezenți la webinar.
              </p>
            </form>
          </>
        )}
      </main>
      <Footer />
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <div>
      <label className="block text-sm font-medium text-[#373A45] mb-1">
        {label} {required && <span className="text-[#E63946]">*</span>}
      </label>
      {children}
    </div>
  );
}
